/**
 * Module dependencies,
 */

import { Breadcrumbs } from 'src/components/core/breadcrumbs';
import { Category } from 'src/types/service';
import { Lead } from 'src/components/layout/homepage/styles';
import { Link } from 'src/components/core/links/link';
import { Svg, useBreakpoint } from '@untile/react-components';
import { Text } from 'src/components/core/text';
import { media } from 'src/styles/utils/media';
import { useMemo } from 'react';
import { useRouteResolver } from 'src/hooks/use-route-resolver';
import { useTranslation } from 'next-i18next';
import iconButton from 'src/assets/svg/button.svg';
import orderBy from 'lodash/orderBy';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  category: Category;
  items: any[];
  subtitle?: string;
  title?: string;
};

/**
 * `List` styled component.
 */

const List = styled.ul`
  --list-columns-size: 1;
  --list-border-color: var(--color-gray200);

  display: grid;
  gap: 0 2rem;
  grid-auto-flow: rows;
  grid-template-columns: repeat(var(--columns-size), 1fr);

  ${media.min.ms`
    --columns-size: 2;
  `}

  ${media.min.lg`
    --columns-size: 3;
  `}
`;

/**
 * `ListItem` styled component.
 */

const ListItem = styled.li`
  align-items: center;
  border-top: 1px solid var(--list-border-color);
  display: flex;
  min-height: 56px;
  padding: 0.5rem 1rem;

  &:last-child {
    border-bottom: 1px solid var(--list-border-color);
  }

  ${media.min.ms`
    &:nth-last-child(-n+2) {
      border-bottom: 1px solid var(--list-border-color);
    }
  `}

  ${media.min.lg`
    &:nth-last-child(-n+3) {
      border-bottom: 1px solid var(--list-border-color);
    }
  `}
`;

/**
 * `StyledLink` styled component.
 */

const StyledLink = styled(Link)`
  align-items: center;
  border: none;
  display: grid;
  grid-auto-flow: column;
  grid-gap: 4px;
  grid-template-columns: 24px 1fr;
  justify-content: center;
  width: 100%;

  & svg {
    color: var(--color-cappuccino400);
    transition: color var(--transition-default);
  }

  &:focus,
  &:hover {
    &,
    & svg {
      color: var(--color-sage600);
    }
  }
`;

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  padding-bottom: var(--vertical-spacing);
`;

/**
 * `EmptyMessage` styled component.
 */

const EmptyMessage = styled(Text).attrs({ variant: 'h5' })`
  align-items: center;
  color: var(--color-gray500);
  display: flex;
  justify-content: center;
  padding: 10vmin 0;
`;

/**
 * `ServicesList` component.
 */

export function ServicesList(props: Props) {
  const { category, items, subtitle, title, ...rest } = props;
  const { t } = useTranslation();
  const resolveRoute = useRouteResolver();
  const isDesktop = useBreakpoint('lg');
  const isTablet = useBreakpoint('ms');
  const gridItemsPerRow = (isDesktop && 3) || (isTablet && 2) || 1;
  const breadcrumbs = useMemo(
    () => [
      {
        label: t('common:terms.services'),
        url: resolveRoute('allServices')
      },
      {
        label: !category ? undefined : t(`common:service.category.${category}`)
      }
    ],
    [category, resolveRoute, t]
  );

  return (
    <Wrapper {...rest}>
      <Breadcrumbs
        data-scroll={'fade-in-up'}
        items={breadcrumbs}
      />

      {title && (
        <Lead
          data-scroll={'fade-in-up'}
          data-scroll-animation-delay={100}
        >
          {title}
        </Lead>
      )}

      {subtitle && (
        <Text
          block
          data-scroll={'fade-in-up'}
          data-scroll-animation-delay={200}
          style={{ marginBottom: '3rem' }}
          variant={'h2'}
        >
          {subtitle}
        </Text>
      )}

      {!items.length ? (
        <EmptyMessage
          data-scroll={'fade-in-up'}
          data-scroll-animation-delay={300}
        >
          {t('common:service.list.empty')}
        </EmptyMessage>
      ) : (
        <List>
          {orderBy(items, ['title'], ['asc'])?.map(
            ({ id, slug, title }, index) => (
              <ListItem
                data-scroll={'fade-in-up'}
                data-scroll-animation-delay={((index % gridItemsPerRow) + 1) * 200}
                data-scroll-offset={'20%'}
                key={id}
              >
                <StyledLink href={resolveRoute('service', { slug })}>
                  <Svg
                    icon={iconButton}
                    size={'24px'}
                  />

                  {title}
                </StyledLink>
              </ListItem>
            )
          )}
        </List>
      )}
    </Wrapper>
  );
}
